import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { SMALL, useDeviceInfo, XLARGE } from "@swa-ui/browser";
import { Heading, Layout, Section } from "@swa-ui/core";
import { classNames } from "@swa-ui/string";

import { applyOverrides } from "../applyOverrides";
import { CallToAction } from "../CallToAction";
import { LayoutSuspense } from "../LayoutSuspense";
import styles from "./PlacementSection.module.scss";

const SPACING_UNITS = {
  DEFAULT: "default",
  EXTRA: "extra",
  NONE: "none",
};

export const PlacementSection = (props) => {
  const {
    background,
    callToAction,
    children,
    className,
    fullWidth,
    heading,
    id,
    layout,
    priority,
  } = props;
  const { screenSize } = useDeviceInfo();
  const [animationToken, setAnimationToken] = useState();

  const sectionContent = (
    <Section className={classNames(styles.section, className)}>
      <div {...getSectionBackgroundProps()}>{renderSection()}</div>
    </Section>
  );

  useEffect(() => {
    setAnimationToken(`placement-section-${id}-${screenSize}`);
  }, [screenSize]);

  return (
    <>
      {priority ? (
        sectionContent
      ) : (
        <LayoutSuspense {...getLayoutSuspenseProps()}>{sectionContent}</LayoutSuspense>
      )}
    </>
  );

  function renderSection() {
    let content;

    if (layout === "compact") {
      content = screenSize === SMALL ? renderSmallCompactSection() : renderCompactSection();
    } else if (layout === "heading") {
      content = renderHeadingSection();
    } else {
      content = renderDefaultSection();
    }

    return content;
  }

  function renderCompactSection() {
    return (
      <>
        <Layout {...getCompactLayoutProps()}>
          {renderHeading()}
          {renderCallToAction()}
        </Layout>
        {children}
      </>
    );
  }

  function renderSmallCompactSection() {
    return (
      <>
        <Layout bottom="medium" sides>
          {renderHeading()}
        </Layout>
        {children}
        <Layout top="medium" sides>
          <div>{renderCallToAction()}</div>
        </Layout>
      </>
    );
  }

  function renderHeadingSection() {
    return <Layout {...getDefaultLayoutProps()}>{renderHeading()}</Layout>;
  }

  function renderDefaultSection() {
    return (
      <>
        {renderHeading()}
        <Layout {...getDefaultLayoutProps()}>{renderChildren()}</Layout>
        {renderCallToAction()}
      </>
    );
  }

  function renderChildren() {
    return (
      children &&
      React.cloneElement(children, {
        className: classNames(children.props.className),
      })
    );
  }

  function renderCallToAction() {
    return callToAction ? <CallToAction {...callToAction} /> : null;
  }

  function renderHeading() {
    return heading ? <Heading {...getHeadingProps()} /> : null;
  }

  function getLayoutSuspenseProps() {
    return {
      animate: true,
      animationToken,
    };
  }

  function getSectionBackgroundProps() {
    const { DEFAULT, EXTRA, NONE } = SPACING_UNITS;
    const { spacing } = props;
    // TODO: Remove explicit applyOverride() calls [JIRA-1318]
    // Currently, we need to call it here because PlacementSection isn't getting the overrides
    // when it's pulling the data from bootstrap. The card reference is to fix/refactor this
    // so that we can then remove the applyOverride() calls here.
    const { color, imagePath, type } = applyOverrides(background, screenSize);
    const { bottom, top } = applyOverrides(spacing, screenSize);

    return {
      className: classNames({
        [styles.paddingTopDefault]: top === DEFAULT || !top,
        [styles.paddingTopExtra]: top === EXTRA,
        [styles.paddingTopNone]: top === NONE,
        [styles.paddingBottomDefault]: bottom === DEFAULT || !bottom,
        [styles.paddingBottomExtra]: bottom === EXTRA,
        [styles.paddingBottomNone]: bottom === NONE,
      }),
      style: {
        ...(type === "image" && { backgroundImage: `url(${imagePath})` }),
        ...(type === "color" && { backgroundColor: color }),
        ...(paddingIsHardcoded(bottom) && { paddingBottom: bottom }),
        ...(paddingIsHardcoded(top) && { paddingTop: top }),
      },
    };

    function paddingIsHardcoded(value) {
      return value && !Object.values(SPACING_UNITS).includes(value);
    }
  }

  function getCompactLayoutProps() {
    return {
      bottom: "medium",
      center: screenSize === XLARGE,
      className: styles.compactHeading,
      sides: fullWidth,
    };
  }

  function getDefaultLayoutProps() {
    return {
      center: screenSize === XLARGE,
      sides: !fullWidth,
    };
  }

  function getHeadingProps() {
    const { color, fontSize, fontWeight, headingLevel = 2, styleLevel, value } = heading ?? {};

    return {
      children: <span style={{ color, fontSize, fontWeight }}>{value}</span>,
      headingLevel,
      styleLevel,
    };
  }
};

PlacementSection.displayName = "PlacementSection";
PlacementSection.propTypes = {
  /** Background image/color content for the section. Defaults to an empty object. */
  background: PropTypes.shape({
    color: PropTypes.string,
    imagePath: PropTypes.string,
    type: PropTypes.oneOf(["color", "image"]),
  }),

  /** Props for Call To Action. */
  callToAction: PropTypes.shape({
    buttonType: PropTypes.string,
    newWindow: PropTypes.bool,
    target: PropTypes.string,
    text: PropTypes.string,
    type: PropTypes.string,
  }),

  /** Content of the section. */
  children: PropTypes.node,

  /** Class configuration. */
  className: PropTypes.string,

  /** Makes the section full width, removing the Grid. */
  fullWidth: PropTypes.bool,

  /** Heading block for the section. */
  heading: PropTypes.shape({
    headingLevel: PropTypes.number,
    styleLevel: PropTypes.number,
    text: PropTypes.string,
  }),

  /** ID of the placement */
  id: PropTypes.string,

  /** Changes the layout structure of this section. */
  layout: PropTypes.oneOf(["compact", "default", "heading"]),
};

PlacementSection.defaultProps = {
  priority: false,
};
